import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, companyAPI } from '../services/api';
import { savePOCData, clearPOCData } from '../utils/pocStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch and store POC data for company users
  const fetchAndStorePOCData = async (user) => {
    try {
      // Only fetch for company role users who have completed intake
      if (user?.role === 'company' && user?.companyId) {
        const response = await companyAPI.getCompanyInfo();
        if (response.data.success && response.data.company) {
          const company = response.data.company;

          // Store POC data in sessionStorage
          const pocData = {
            pocFirstName: company.pocFirstName || company.pocName?.split(' ')[0] || '',
            pocLastName: company.pocLastName || company.pocName?.split(' ').slice(1).join(' ') || '',
            pocEmail: company.pocEmail || '',
            pocCountryCode: company.pocCountryCode || '+91',
            pocPhone: company.pocPhone || ''
          };

          savePOCData(pocData);
        }
      }
    } catch (error) {
      console.error('Failed to fetch POC data:', error);
      // Non-critical error, don't block auth flow
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Verify token with backend
        const response = await authAPI.verify();
        if (response.data.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          // Fetch and store POC data for validation
          await fetchAndStorePOCData(response.data.user);
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        // If it's a network error or the refresh also failed, clear auth
        // The interceptor will handle 401s and attempt refresh
        if (error.response?.status === 401 || !error.response) {
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
          setUser(null);
          clearPOCData(); // Clear POC data on auth failure
        }
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (email, phone, authMethod = 'email') => {
    try {
      setError(null);
      const payload = authMethod === 'email'
        ? { email, authMethod }
        : { phone, authMethod };

      const response = await authAPI.login(payload);
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, message };
    }
  };

  const verifyOTP = async (identifier, otp, authMethod = 'email') => {
    try {
      setError(null);
      const payload = authMethod === 'email'
        ? { email: identifier, otp, authMethod }
        : { phone: identifier, otp, authMethod };

      const response = await authAPI.verifyOTP(payload);
      const { user, accessToken } = response.data;

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('accessToken', accessToken);
      setUser(user);

      // Fetch and store POC data after successful login
      await fetchAndStorePOCData(user);

      return { success: true, user };
    } catch (err) {
      const message = err.response?.data?.message || 'OTP verification failed';
      setError(message);
      return { success: false, message };
    }
  };

  const resendOTP = async (identifier, authMethod = 'email') => {
    try {
      setError(null);
      const payload = authMethod === 'email'
        ? { email: identifier, authMethod }
        : { phone: identifier, authMethod };

      const response = await authAPI.resendOTP(payload);
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to resend OTP';
      setError(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      clearPOCData(); // Clear POC data on logout
      setUser(null);
    }
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const value = {
    user,
    loading,
    error,
    login,
    verifyOTP,
    resendOTP,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCompany: user?.role === 'company',
    isNonDemo: user?.role === 'non-demo',
    isSponsor: user?.role === 'sponsor',
    hasLimitedAccess: user?.role === 'non-demo' || user?.role === 'sponsor'
  };

  // Show loading screen while verifying auth
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Verifying Authentication...</h2>
          <p className="text-gray-500 mt-2">Please wait while we check your session</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};